import { useFetch } from "@/lib/content";
import { useSEO } from "@/lib/seo";
import { Eyebrow, Reveal, CtaButton, ParallaxImage } from "@/components/site/Motion";
import PressRow from "@/components/site/PressRow";

const HERO_IMG =
  "https://images.unsplash.com/photo-1504711434969-e33886168f5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Media() {
  const { data: items } = useFetch("/media");

  useSEO({
    title: "Media — RenB in the News | RenB Solutions",
    description:
      "Press coverage of RenB Solutions, including The Hindu, Deccan Chronicle and The New Indian Express reporting on the GSIB–RenB startup incubator in Visakhapatnam.",
    path: "/media",
  });

  return (
    <div data-testid="media-page">
      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-28 md:px-10 md:pb-20 md:pt-32 lg:grid-cols-12 lg:items-stretch lg:gap-8">
          <Reveal className="lg:col-span-7 lg:self-center">
            <Eyebrow>Media Archive</Eyebrow>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
              RenB in the news<span className="text-progress">.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-obsidian/70 md:text-lg">
              Selected media coverage of RenB Solutions&apos; work in building one of India&apos;s largest startup
              ecosystems. Working with academic institutions, investors and industry leaders, we created a platform that
              took founders from first idea to funded, operating business.
            </p>
          </Reveal>
          <div className="hidden lg:col-span-5 lg:block">
            <ParallaxImage
              src={HERO_IMG}
              alt="Close-up of a business newspaper"
              className="h-full min-h-[460px] border border-ash"
              imgClassName="grayscale"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-ash bg-white/40">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-16 md:grid-cols-12 md:px-10 md:py-24">
          <Reveal className="md:col-span-7">
            <Eyebrow>2015–2016 · GSIB / RenB Startup Incubator</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight md:text-5xl">
              Building an entrepreneurship ecosystem
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-obsidian/70">
              This archive brings together newspaper and digital-media coverage from the launch and development of the
              GSIB–RenB startup incubator. The reports document the MoU, the incubator launch, the proposed support model,
              and the participation of academic and industry leaders.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-4 md:col-start-9">
            <aside data-testid="media-archive-note" className="border border-obsidian/15 bg-paper p-8">
              <p className="text-[13px] font-bold uppercase tracking-[0.2em] text-progress">Archive note</p>
              <p className="mt-4 text-sm leading-relaxed text-obsidian/70">
                Publication names, dates and bylines are stated as visible in the supplied clippings. Where an original
                clipping is cropped or unclear, we deliberately identify the information as unavailable rather than
                reconstructing it.
              </p>
            </aside>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-8 md:px-10 md:py-12">
        {items && items.length === 0 && (
          <p data-testid="media-empty" className="py-24 text-center text-slategray">
            Coverage will appear here as it is published.
          </p>
        )}
        {(items || []).map((item, i) => (
          <PressRow key={item.id} item={item} index={i} />
        ))}
      </section>

      <section className="border-t border-ash bg-obsidian text-paper">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-7">
            <Eyebrow light>Press &amp; speaking</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight md:text-5xl">
              Writing about SME growth, transformation or founder leadership?
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-paper/70">
              RenB&apos;s partners are available for comment, interviews and speaking engagements.
            </p>
          </div>
          <div className="flex items-end md:col-span-4 md:col-start-9 md:justify-end">
            <CtaButton href="/contact" testid="media-press-cta">
              Contact us
            </CtaButton>
          </div>
        </div>
      </section>

      <p className="mx-auto max-w-[1440px] px-5 py-8 text-[12px] text-slategray md:px-10">
        Original newspaper clippings are retained for archival reference. Media coverage reproduced here is presented as a
        historical record of RenB&apos;s activities and partnerships.
      </p>
    </div>
  );
}
